"use client";

import { useState, useMemo } from "react";
import { Table, Button, Space } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import OrderDetailsModal from "./OrderDetailsModal";
import { formatDateTime } from "../utils/orderHelpers";
import { StatusBadge } from "./status-badge";
import { Banknote, CreditCard, Landmark, Wallet, ShoppingBag, Bike, HelpCircle, UtensilsCrossed } from "lucide-react";

export default function OrdersTable({
  orders,
  allOrders,
  OrderStatuses,
  onRiderChange,
  onBranchTransfer,
  onDeliveryTimeUpdate,
  newOrderIds,
  riders,
  branches
}) {

  const [selectedOrderToken, setSelectedOrderToken] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedOrder = useMemo(
    () => allOrders.find(o => o.OrderToken === selectedOrderToken),
    [allOrders, selectedOrderToken]
  );

  const handleViewOrder = (order) => {
    setSelectedOrderToken(order.OrderToken);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrderToken(null);
  };

  // Table columns definition
  const columns = [
    {
      title: "Order Number",
      dataIndex: "OrderToken",
      key: "OrderToken",
      render: (text) => <span className="font-medium">{text || "N/A"}</span>,
    },
    {
      title: "Branch Name",
      dataIndex: "BranchName",
      key: "BranchName",
      render: (branchName) => branchName || "Main",
    },
    {
      title: "Phone",
      dataIndex: ["CustomerDetails", "MobileNumber"],
      key: "phone",
      render: (phone) => phone || "N/A",
    },
    {
      title: "Area Name",
      dataIndex: "AreaName",
      key: "areaName",
      render: (areaName) => areaName || "N/A",
    },
    {
      title: "Customer Name",
      dataIndex: ["CustomerDetails", "FullName"],
      key: "customerName",
      render: (name) => name || "N/A",
    },
    {
      title: "Order Date Time",
      dataIndex: "OrderTime",
      key: "OrderTime",
      render: (time) => formatDateTime(time),
      // sorter: (a, b) => new Date(a.OrderTime) - new Date(b.OrderTime),
      defaultSortOrder: "descend",
    },
    {
      title: "Amount",
      dataIndex: "AmountWithGst",
      key: "AmountWithGst",
      render: (_, record) => {
        const grandTotal = Math.round((record.AmountWithGst || 0) - (record.TotalDiscount) + (record.DeliveryCharges || 0));
        return <span className="font-semibold">Rs. {grandTotal.toLocaleString()}</span>;
      },
    },
    {
      title: "Payment Method",
      dataIndex: "PaymentMethod",
      key: "PaymentMethod",
      render: (_, record) => {
        const method = record.CustomerDetails?.PaymentMethod;

        const paymentIcons = {
          "cash": { icon: <Banknote size={15} />, color: "text-emerald-600 bg-emerald-50" },
          "card": { icon: <CreditCard size={15} />, color: "text-blue-600 bg-blue-50" },
          "online": { icon: <Landmark size={15} />, color: "text-purple-600 bg-purple-50" },
          "bank transfer": { icon: <Landmark size={15} />, color: "text-purple-600 bg-purple-50" },
          "wallet": { icon: <Wallet size={15} />, color: "text-orange-600 bg-orange-50" },
        };


        const match = paymentIcons[method?.toLowerCase()];
        const { icon, color } = match || { icon: <HelpCircle size={15} />, color: "text-gray-500 bg-gray-100" };

        return (
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${color}`}>
            {icon}
            {method || "N/A"}
          </span>
        );
      },
    },
    {
      title: "Order Type",
      dataIndex: "OrderType",
      key: "OrderType",
      render: (OrderType) => {
        const orderTypeIcons = {
          "pickup": { icon: <ShoppingBag size={15} />, color: "text-amber-600 bg-amber-50" },
          "delivery": { icon: <Bike size={15} />, color: "text-blue-600 bg-blue-50" },
          "dine in": { icon: <UtensilsCrossed size={15} />, color: "text-rose-600 bg-rose-50" },
          "takeaway": { icon: <ShoppingBag size={15} />, color: "text-amber-600 bg-amber-50" },
        };


        const match = orderTypeIcons[OrderType?.toLowerCase()];
        const { icon, color } = match || { icon: <HelpCircle size={15} />, color: "text-gray-500 bg-gray-100" };

        return (
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${color}`}>
            {icon}
            {OrderType || "N/A"}
          </span>
        );
      },
    },

    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      render: (status) => (
        <StatusBadge status={status || "Pending"} />
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewOrder(record)}
            className="flex items-center justify-center"
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={orders}
        rowKey={(record) => record.OrderNumber || Math.random()}
        rowClassName={(record) =>
          newOrderIds?.has(record.OrderNumber)
            ? 'animate-highlight-pulse bg-yellow-100 border-l-4 border-l-yellow-500'
            : ''
        }
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} orders`,
        }}
        className="rounded-md"
        bordered
        size="middle"
        locale={{
          emptyText: (
            <div className="py-8">
              <p className="text-lg font-medium text-slate-500">No orders yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Orders will appear here in real-time
              </p>
            </div>
          ),
        }}
      />

      {isModalOpen && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={handleCloseModal}
          OrderStatuses={OrderStatuses}
          onRiderChange={onRiderChange}
          onBranchTransfer={onBranchTransfer}
          onDeliveryTimeUpdate={onDeliveryTimeUpdate}
          riders={riders}
          branches={branches} />
      )}
    </>
  );
}